import useToast from '@hooks/ui/useToast';
import { DiscountCouponRow } from '@models/discount';
import { OrderDiscount, OrderLine } from '@models/order';
import orderWithTotalAmount from '@providers/OrderProvider/utils/orderWithTotalAmount';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import { orderActions } from '@store/order/orderSlice';
import { useDispatch } from 'react-redux';

function useCouponControl() {
    const state = useCalculatedRequest();
    const { orderLines, totalAmount } = state;
    const dispatch = useDispatch();
    const toast = useToast();

    const removeCoupon = (couponId: string) => {
        dispatch(orderActions.removeCoupon(couponId));
    };

    const toggleDiscountCoupon = (coupon: DiscountCouponRow, remove: boolean, orderLine?: OrderLine) => {
        removeCoupon(coupon.id);
        if (remove) {
            return;
        }
        const newDiscount: OrderDiscount[] = [];

        const item = orderLines
            .find((line) => line.shopItemId === orderLine?.shopItemId)!;

        const discountAmount = (() => {
            // 상품 쿠폰
            if (item) {
                const options = [...item.defaultOptions, ...item.personalOptions]
                    .map((option) => option.options)
                    .flat();
                const optionPrice = options
                    .map((option) => option.quantity * option.optionPrice)
                    .reduce((a, b) => a + b, 0);
                const totalPrice = item.itemPrice + optionPrice;

                if (coupon.discountMethod === 'RATE') {
                    return Math.floor((totalPrice * (coupon.discountValue! / 100)));
                }
                if (coupon.discountMethod === 'BOGO') {
                    return totalPrice;
                }
                return Math.min(totalPrice, coupon.discountValue!);
            }
            const calculatedTotal = orderWithTotalAmount({
                ...state, discounts: [],
            }).totalAmount;
            // 주문 쿠폰
            if (coupon.discountMethod === 'RATE') {
                const discountOrder = Math.floor((calculatedTotal * (coupon.discountValue! / 100)));
                return Math.min(coupon.maxDiscountAmount!, discountOrder);
            }
            return Math.min(coupon.discountValue!, calculatedTotal);
        })();

        // 최소 주문금액 체크
        if (coupon.minOrderAmount && coupon.minOrderAmount > totalAmount) {
            console.log({ totalAmount });
            toast.error(`최소 주문 금액 ${coupon.minOrderAmount}원 이상일 때 사용 가능합니다.`);
            return;
        }
        const options = orderLine
            ? [...orderLine.defaultOptions, ...orderLine.personalOptions]
                .map((it) => it.options)
                .flat() : undefined;

        // 같은 옵션이 있는지 체크하기 위해 사용
        const optionIds = options?.map((it) => it.shopOptionId);

        const optionKey = options
            ?.map((it) => `${it.posId}x${it.quantity}`)
            .join(':');
        const itemKey = [item?.posId, optionKey]
            .filter((it) => !!it)
            .join(':');

        newDiscount.push({
            type: 'COUPON',
            couponId: coupon.id,
            couponName: coupon.title,
            discountScope: orderLine?.shopItemId ? 'ITEM' : 'ORDER',
            discountAmount,
            itemId: orderLine?.shopItemId,
            itemName: item?.itemName,
            optionIds,
            itemKey: coupon.discountTarget === 'ITEM' ? itemKey : undefined,
        });

        dispatch(orderActions.setDiscounts(newDiscount));
    };

    const clearDiscountCoupon = () => {
        dispatch(orderActions.clearCoupons());
    };

    return {
        toggleDiscountCoupon,
        clearDiscountCoupon,
    };
}

export default useCouponControl;
